import React, { useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import { InputWrapper } from "common/components/Wrapper/InputBase";
import {
  DropdownButton,
  DropdownItem,
  DropdownList,
} from "common/components/Field/new/DropdownComponents";
import { TextInputWithoutValid } from "common/components/Input/TextInputWithoutValid";
import { useDynamicDropdownList } from "common/hooks/useDynamicDropdownList";

const SearchInputWrapper = styled.div`
  padding: 0.5rem;
  border-bottom: 1px solid var(--color-border, #e0e0e0);
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const NoResultsMessage = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--color-text-secondary, #666);
  font-size: var(--fontSize-14);
`;

export const ComboBoxWithoutValid = ({
  label,
  caption,
  icon,
  placeholder,
  searchPlaceholder = "검색...",
  value,
  itemList,
  onChange,
  disabled,
  noResultsText = "검색 결과가 없습니다",
  forceError,
  ...rest
}) => {
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const findItemFromList = (target) => {
    const targetValue = typeof target === "object" ? target?.value : target;
    return itemList?.find((item) => item?.value === targetValue);
  };

  const selectedItem = useMemo(() => {
    return findItemFromList(value);
  }, [value, itemList]);

  const filteredItemList = useMemo(() => {
    if (!searchQuery.trim()) return itemList;
    const query = searchQuery.toLowerCase().trim();
    return itemList?.filter((item) => {
      const text = item?.text?.toLowerCase() || "";
      const val = String(item?.value || "").toLowerCase();

      // 순서대로 문자 포함 (중간 생략 허용)
      const fuzzyMatch = (str, pattern) => {
        let patternIdx = 0;
        for (let i = 0; i < str.length && patternIdx < pattern.length; i++) {
          if (str[i] === pattern[patternIdx]) {
            patternIdx++;
          }
        }
        return patternIdx === pattern.length;
      };

      return fuzzyMatch(text, query) || fuzzyMatch(val, query);
    });
  }, [itemList, searchQuery]);

  const handleSelect = (option) => {
    setOpen(false);
    setSearchQuery("");
    onChange?.(option);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [open]);

  const { dropdownListStyle, updatePosition } = useDynamicDropdownList(
    dropdownRef,
    {
      itemCount: filteredItemList?.length || 0,
      open,
    },
  );

  const handleToggle = () => {
    updatePosition();
    setOpen((prev) => !prev);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && filteredItemList?.length > 0) {
      handleSelect(filteredItemList[0]);
    }
    if (e.key === "Escape") {
      setOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <InputWrapper
      ref={dropdownRef}
      label={label}
      caption={caption}
      icon={icon}
      error={forceError}
      {...rest}>
      {open ? (
        <TextInputWithoutValid
          ref={searchInputRef}
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={setSearchQuery}
          onKeyDown={handleSearchKeyDown}
        />
      ) : (
        <DropdownButton
          component={selectedItem?.component}
          text={selectedItem?.text ?? ""}
          icon={selectedItem?.icon ?? ""}
          placeholder={placeholder}
          onClick={handleToggle}
          isListOpen={open}
          error={forceError}
          disabled={disabled}
        />
      )}
      {open && (
        <DropdownList style={dropdownListStyle}>
          {filteredItemList?.length > 0 ? (
            filteredItemList.map((item, index) => (
              <DropdownItem
                key={`combobox-item-${index}`}
                item={item}
                onClick={() => handleSelect(item)}
                selected={selectedItem?.key === item.key}
              />
            ))
          ) : (
            <NoResultsMessage>{noResultsText}</NoResultsMessage>
          )}
        </DropdownList>
      )}
    </InputWrapper>
  );
};
